import dayjs, { Dayjs } from "dayjs";
import { hasRealDateOfBirth } from "./v2/completion";

// Client-configurable birthday greeting shown on check-in, parsed from the
// checkin.birthdayMessage client_setting. Disabled by default so it only shows
// for clients that opt in (e.g. Charing Cross).

export interface BirthdayMessageConfig {
    enabled: boolean;
    template: string;
}

export const DEFAULT_BIRTHDAY_MESSAGE: BirthdayMessageConfig = {
    enabled: false,
    template: '🎉 Happy Birthday, {{firstName}}!',
};

export function parseBirthdayMessage(json: string | undefined | null): BirthdayMessageConfig {
    if (!json) return DEFAULT_BIRTHDAY_MESSAGE;
    try {
        const p = JSON.parse(json);
        if (!p || typeof p !== 'object') return DEFAULT_BIRTHDAY_MESSAGE;
        return {
            enabled: typeof p.enabled === 'boolean' ? p.enabled : false,
            template: typeof p.template === 'string' && p.template.trim()
                ? p.template
                : DEFAULT_BIRTHDAY_MESSAGE.template,
        };
    } catch {
        return DEFAULT_BIRTHDAY_MESSAGE;
    }
}

// Returns the interpolated greeting when the message is enabled and today is the
// member's birthday (matching month + day); otherwise null. {{firstName}} is
// taken from the first token of the member's name.
export function birthdayGreeting(
    config: BirthdayMessageConfig,
    member: { name: string; dateOfBirth: Date | string | null | undefined },
    now: Dayjs = dayjs(),
): string | null {
    // A not-provided date of birth arrives as the 0001-01-01 sentinel, which would
    // otherwise match 1 January and wish a happy birthday to every member who has
    // never given one. Guest signup, Quick Save and the importers all produce that
    // sentinel now, so this would be a New Year's Day greeting for the whole cohort.
    if (!config.enabled || !hasRealDateOfBirth(member.dateOfBirth)) return null;
    const dob = dayjs(member.dateOfBirth);
    if (!dob.isValid()) return null;
    if (dob.date() !== now.date() || dob.month() !== now.month()) return null;
    const firstName = (member.name ?? '').trim().split(/\s+/)[0] ?? '';
    return config.template.replace(/\{\{firstName\}\}/g, firstName);
}
